/**
 * ログイン方法（Supabase の identities）の読み取り。Issue #118。
 *
 * identity は「メール + パスワード」「Google」などログイン手段ごとに 1 つずつ付く。
 * Google のみの人はパスワードを持たず、ログイン用メールアドレスも Google 側で決まるので、
 * 設定画面でパスワード変更・ログイン用メールアドレス変更を出さない。
 */

type IdentityLike = {
  identity_id: string
  provider: string
  identity_data?: { [key: string]: unknown }
}

type GoogleAccount = { identityId: string; email: string | null }

export type LoginMethods = {
  hasPassword: boolean
  isGoogleOnly: boolean
  googleAccounts: GoogleAccount[]
}

export function describeLoginMethods(
  identities: readonly IdentityLike[] | undefined,
): LoginMethods {
  // 取れないときは「パスワードあり」として扱い、欄を出す側に倒す（隠すと変更手段が消える）
  if (!identities) return { hasPassword: true, isGoogleOnly: false, googleAccounts: [] }
  const hasPassword = identities.some((i) => i.provider === 'email')
  const googleAccounts = identities
    .filter((i) => i.provider === 'google')
    .map((i) => ({ identityId: i.identity_id, email: identityEmail(i) }))
  return { hasPassword, isGoogleOnly: !hasPassword && googleAccounts.length > 0, googleAccounts }
}

export type GoogleSwitchPlan<T> =
  | { kind: 'unlink'; oldIdentity: T; newEmail: string | null }
  | { kind: 'unchanged' }
  | { kind: 'done'; newEmail: string | null }

/**
 * Google の紐付けから戻ってきた後に、何をすれば切り替えが終わるか。
 *
 * 切り替えは「新しい Google を足す → 古い Google を外す」の順で行う。逆にすると、
 * Google のみの人は外した瞬間にログイン手段が無くなる（Supabase も最後の 1 つは外させない）。
 */
export function planGoogleSwitchCompletion<T extends IdentityLike>(
  identities: readonly T[],
  replaceIdentityId: string | null,
): GoogleSwitchPlan<T> {
  const google = identities.filter((i) => i.provider === 'google')
  const added = google.find((i) => i.identity_id !== replaceIdentityId)
  const old = google.find((i) => i.identity_id === replaceIdentityId)
  if (!added) return { kind: 'unchanged' }
  const newEmail = identityEmail(added)
  return old ? { kind: 'unlink', oldIdentity: old, newEmail } : { kind: 'done', newEmail }
}

function identityEmail(identity: IdentityLike): string | null {
  const email = identity.identity_data?.email
  return typeof email === 'string' ? email : null
}
